import { create } from 'zustand';
import { EntityName } from 'shared/domain/entityName';
import { compareProjectNames } from 'shared/domain/project/compareProjectNames';
import { projectClient } from 'shared/domain/project/projectClient';
import type { ProjectModel } from 'shared/domain/project/types/model';
import { findById } from 'shared/utils/id/findById';
import { getProjectId, getProjectStoreState } from 'store/project';
import { createSelectors, StoreName, withDevtools } from '../config';
import { createEntityCollectionActions } from '../entityCollection';
import { createDataActions, createInitialData } from '../types';
import type { ProjectsStore } from './types';

/** The selected project keeps the name, timezone, … the list has for it. */
function refreshSelectedProjectMetadata(projects: ProjectModel[]): void {
  const selectedProject = findById(projects, getProjectId(), 'projects store selected project');
  if (selectedProject) {
    getProjectStoreState().actions.patchMetadata(selectedProject);
  }
}

/** Projects of the logged-in user, loaded and kept current through `projectClient`. */
const useProjectsStoreBase = create<ProjectsStore>()(
  withDevtools((set, get) => {
    const dataActions = createDataActions<ProjectModel, ProjectsStore>(set);

    return {
      data: createInitialData<ProjectModel>(),
      syncFailed: false,

      actions: {
        ...dataActions,
        ...createEntityCollectionActions(
          {
            client: projectClient,
            entityName: EntityName.projects,
            onLoadFailed: () => set({ syncFailed: true }),
          },
          () => get().actions
        ),

        setItems: (items) => {
          const sortedItems = [...items].sort(compareProjectNames);
          dataActions.setItems(sortedItems);
          set({ syncFailed: false });
          refreshSelectedProjectMetadata(sortedItems);
        },
        reset: () => {
          dataActions.reset();
          set({ syncFailed: false });
        },
        reloadShowingLoading: () => {
          get().actions.setLoading(true);
          get().actions.requestReload();
        },
      },
    };
  }, StoreName.Projects)
);

export const useProjectsStore = createSelectors(useProjectsStoreBase);

export const getProjectsStoreState = (): ProjectsStore => useProjectsStore.getState();
